import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Check, CaretUpDown, Plus } from "@phosphor-icons/react";
import { NuovoClienteDialog } from "./NuovoClienteDialog";

/** Nome visualizzato: aziende con la ragione sociale, privati "Cognome Nome". */
export const nomeCliente = (c) => {
  if (!c) return "";
  if (c.ragione_sociale) return c.ragione_sociale;
  if (c.tipo === "azienda") return c.nome || "";
  return [c.cognome, c.nome].filter(Boolean).join(" ").trim() || "—";
};

const chiave = (c) => (c.ragione_sociale || (c.tipo === "azienda" ? c.nome : `${c.cognome || ""} ${c.nome || ""}`) || "").trim();
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Clienti in ordine alfabetico per cognome (aziende per ragione sociale). */
export const ordinaClienti = (clienti = []) => [...clienti].sort((a, b) => chiave(a).localeCompare(chiave(b), "it", { sensitivity: "base" }));

/** Selettore committente con ricerca in alto e ordine alfabetico per cognome. */
export function ClientePicker({ clienti = [], value = "", onChange, onCreato, placeholder = "Scegli committente…", vuotoLabel = "— Nessuno —", escludi = [], className = "", size = "sm", disabled = false, testId = "cliente-picker", nuovo = true }) {
  const [open, setOpen] = useState(false);
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const [creati, setCreati] = useState([]);
  const tutti = useMemo(() => [...clienti, ...creati.filter((n) => !clienti.some((c) => c.id === n.id))], [clienti, creati]);
  const lista = useMemo(() => ordinaClienti(tutti.filter((c) => !escludi.includes(c.id))), [tutti, escludi]);
  const sel = tutti.find((c) => c.id === value);
  const creato = (c) => { setCreati((l) => [...l, c]); onCreato?.(c); onChange(c.id); };
  const h = size === "xs" ? "h-8 text-xs" : "h-9 text-sm";
  const scegli = (id) => { onChange(id); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className={`${h} w-full inline-flex items-center justify-between gap-1 rounded-md border px-3 text-left truncate bg-white ${sel ? "border-slate-300 text-slate-800" : "border-slate-200 text-slate-500"} disabled:opacity-50 ${className}`} data-testid={testId} title={sel ? nomeCliente(sel) : placeholder}>
          <span className="truncate">{sel ? nomeCliente(sel) : placeholder}</span>
          <CaretUpDown size={12} className="shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command loop filter={(v, s) => (norm(v).includes(norm(s)) ? 1 : 0)}>
          <CommandInput placeholder="Cerca per cognome, nome o CF…" className="h-8 text-xs" autoFocus data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty className="py-3 text-xs text-slate-500 text-center">Nessun committente trovato</CommandEmpty>
            <CommandGroup>
              {vuotoLabel && (
                <CommandItem value="__nessuno__" onSelect={() => scegli("")} className="text-xs text-slate-500" data-testid={`${testId}-opt-nessuno`}>
                  <Check size={12} className={`mr-1.5 ${!value ? "opacity-100" : "opacity-0"}`} />{vuotoLabel}
                </CommandItem>
              )}
              {lista.map((c) => (
                <CommandItem key={c.id} value={`${nomeCliente(c)} ${c.codice_fiscale || ""} ${c.partita_iva || ""}`} onSelect={() => scegli(c.id)} className="text-xs" data-testid={`${testId}-opt-${c.id}`}>
                  <Check size={12} className={`mr-1.5 shrink-0 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{nomeCliente(c)}</span>
                  {c.tipo === "azienda" && <span className="ml-auto text-[9px] font-mono uppercase text-slate-400">azienda</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {nuovo && (
            <button type="button" onClick={() => { setOpen(false); setNuovoOpen(true); }} className="w-full flex items-center gap-1.5 border-t border-slate-200 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50" data-testid={`${testId}-nuovo`}>
              <Plus size={12} weight="bold" /> Nuovo cliente
            </button>
          )}
        </Command>
      </PopoverContent>
      {nuovo && <NuovoClienteDialog open={nuovoOpen} onOpenChange={setNuovoOpen} onCreato={creato} testId={`${testId}-nuovo-cliente`} />}
    </Popover>
  );
}
